import { Typography } from "@material-ui/core";
import { EmployeeDepartmentDto, ListItemDto } from "Api/Api";
import { EmployeeFormData } from "Components/HumanResources/Employees/EmployeeEdit/EmployeeEditForm";
import { DefaultButtonWithTooltip } from "Components/Shared/Buttons/DefaultButtonWithTooltip";
import { PlusIcon } from "Components/Shared/Icons/PlusIcon";
import { TrashCanIcon } from "Components/Shared/Icons/TrashCanIcon";
import { Autocomplete } from "Components/Shared/Inputs/Autocomplete";
import * as React from "react";
import { FieldError, UseFormMethods } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import { getHumanResourcesDepartmentsAutocompleteAsync } from "State/HumanResources/HumanResourcesReducer";
import { RootStateType } from "State/Store";
import styled from "styled-components";
import { Resources, useResource } from "Translations/Resources";
import { formatDate } from "Utils/DateUtils";
import { nameof } from "Utils/ObjectUtils";
import { formatISO } from "date-fns";
import { AutoAdderRegister } from "Hooks/useAutoAdder";

type Props = {
  form: UseFormMethods<EmployeeFormData>;
  autoAdderRegister: AutoAdderRegister;
};

const StyledWrapper = styled.div`
  display: grid;
  grid-template-columns: minmax(auto, 450px) 1fr;
  gap: ${props => props.theme.spacing(1, 3)};
  align-items: center;
  margin-bottom: ${props => props.theme.spacing(5)}px;

  @media (max-width: 1000px) {
    grid-template-columns: 1fr;
    gap: 0;
  }
`;

const StyledPositionsWrapper = styled.div`
  display: grid;
  gap: ${props => props.theme.spacing(0.75, 0)};

  & > div {
    padding: ${props => props.theme.spacing(0.25)}px;
    position: relative;
  }
`;

const StyledTrashIcon = styled.div`
  position: absolute;
  right: 0;
  bottom: 3px;

  svg {
    height: 20px;
  }
`;

const StyledDate = styled.div`
  border-bottom: 1px solid ${props => props.theme.colors.border};
  color: ${props => props.theme.palette.text.secondary};
  font-size: ${props => props.theme.typography.h5.fontSize}px;
  padding-bottom: ${props => props.theme.spacing(0.75)}px;
`;

const StyledNewDepartmentWrapper = styled.div`
  display: grid;
  grid-template-columns: 1fr auto;
  align-items: flex-end;
  gap: ${props => props.theme.spacing(0, 2)};
  margin-top: ${props => props.theme.spacing(1)}px;
  margin-right: -5px;
`;

const StyledError = styled.div`
  color: ${props => props.theme.colors.red};
  font-style: italic;
  padding-bottom: ${props => props.theme.spacing(1)}px;
  align-self: flex-end;

  @media (max-width: 1000px) {
    & > * {
      font-size: 12px;
    }
  }
`;

export const EmployeeEditDepartments: React.FunctionComponent<Props> = props => {
  const { t } = useResource();
  const dispatch = useDispatch();
  const { form } = props;
  const { register, watch, setValue } = form;
  const { departments } = watch();

  React.useEffect(() => {
    register({ name: nameof<EmployeeFormData>("departments") });
  }, [register]);

  props.autoAdderRegister("departments", () => {
    if (departmentToAdd) {
      addDepartment();
    }
    return true;
  });

  const [
    departmentToAdd,
    setDepartmentToAdd,
  ] = React.useState<ListItemDto | null>(null);
  const [
    departmentToAddError,
    setDepartmentToAddError,
  ] = React.useState<FieldError | null>(null);
  const {
    departmentsAutocompleteList,
    isDepartmentsAutocompleteLoading,
  } = useSelector((e: RootStateType) => e.humanResources);

  const onSearch = (text: string) => {
    dispatch(getHumanResourcesDepartmentsAutocompleteAsync.request(text));
  };

  const handleRemove = (department: EmployeeDepartmentDto) => {
    setValue(
      nameof<EmployeeFormData>("departments"),
      departments?.filter(x => x !== department),
    );
  };

  const validate = () => {
    if (!departmentToAdd) {
      setDepartmentToAddError({
        type: "required",
        message: t(
          Resources.HumanResources.EmployeeEdit.Departments.Validation,
        ),
      });
      return false;
    }
    if (
      departments.filter(x => x.departmentID === departmentToAdd.itemID)
        .length > 0
    ) {
      setDepartmentToAddError({
        type: "required",
        message: t(
          Resources.HumanResources.EmployeeEdit.Departments.ValidationWasAdded,
        ),
      });
      return false;
    }
    return true;
  };

  const addDepartment = () => {
    if (!departmentToAdd) {
      return;
    }

    departments.push({
      departmentID: departmentToAdd.itemID,
      departmentName: departmentToAdd.name,
      dateFrom: formatISO(new Date()),
      employeeDepartmentID: 0,
    });

    setValue(nameof<EmployeeFormData>("departments"), departments, {
      shouldValidate: true,
    });

    setDepartmentToAdd(null);
  };

  return (
    <div>
      <div className={"form-title"}>
        {t(Resources.HumanResources.EmployeeEdit.Departments.Label)}
      </div>
      <StyledWrapper>
        <StyledPositionsWrapper>
          {departments?.map(dep => (
            <React.Fragment key={dep.departmentID}>
              <div>
                <Typography>{dep.departmentName}</Typography>
                <StyledDate>
                  {`${t(
                    Resources.HumanResources.EmployeeEdit.Departments.DateFrom,
                  )}: ${formatDate(dep.dateFrom)}`}
                </StyledDate>
                <StyledTrashIcon>
                  <DefaultButtonWithTooltip
                    title={t(
                      Resources.HumanResources.EmployeeEdit.Departments.Remove,
                    )}
                    onClick={() => handleRemove(dep)}
                  >
                    <TrashCanIcon />
                  </DefaultButtonWithTooltip>
                </StyledTrashIcon>
              </div>
            </React.Fragment>
          ))}
        </StyledPositionsWrapper>
        <div></div>

        <StyledNewDepartmentWrapper>
          <Autocomplete
            value={departmentToAdd}
            onSearch={onSearch}
            isLoading={isDepartmentsAutocompleteLoading}
            options={departmentsAutocompleteList ?? []}
            onChange={val => {
              setDepartmentToAdd(val);
              setDepartmentToAddError(null);
            }}
            fieldError={departmentToAddError ?? undefined}
          />
          <DefaultButtonWithTooltip
            title={t(Resources.HumanResources.EmployeeEdit.Departments.Add)}
            onClick={_ => {
              if (validate()) {
                addDepartment();
              }
            }}
          >
            <PlusIcon />
          </DefaultButtonWithTooltip>
        </StyledNewDepartmentWrapper>

        <StyledError>
          {departmentToAddError && (
            <Typography>{departmentToAddError?.message}</Typography>
          )}
        </StyledError>
      </StyledWrapper>
    </div>
  );
};
